import { type ReactNode, useEffect, useRef, useState } from 'react'
import { frameCopy } from '@/copy'
import { cx } from '../cx'
import { Icon } from '../Icon/Icon'
import styles from './CodeBlock.module.css'

const COPIED_MS = 1500

export function CodeBlock({
  title,
  caption,
  copyable = true,
  maxLines = 12,
  wrap = false,
  children,
  className,
}: {
  className?: string
  copyable?: boolean
  maxLines?: number
  wrap?: boolean
  children: ReactNode
} /** `title` is the id, in mono; `caption` a word beside it, in the sans.
      A block with no id carries the word alone (CodeBlock card: Anatomy). */ & (
  | { title: string; caption?: string }
  | { title?: undefined; caption: string }
)) {
  const ref = useRef<HTMLPreElement>(null)
  // Counts successful copies; 0 is idle. A new count restarts the timer, and
  // its parity re-keys the status node so a repeat copy is announced again.
  const [copies, setCopies] = useState(0)
  const copied = copies > 0

  useEffect(() => {
    if (copies === 0) return
    const timer = setTimeout(() => setCopies(0), COPIED_MS)
    return () => clearTimeout(timer)
  }, [copies])

  const what = [title, caption].filter(Boolean).join(' ')

  async function copy() {
    try {
      await navigator.clipboard.writeText(ref.current?.textContent ?? '')
      setCopies((n) => n + 1)
    } catch {
      // A refused write shows nothing: no false "Copied" and no error (D36).
    }
  }

  return (
    <div className={cx(styles.codeBlock, className)}>
      <div className={styles.header}>
        <span className={styles.label}>
          {title && <span className={styles.title}>{title}</span>}
          {caption && (
            <span>
              {title && frameCopy.codeBlock.captionJoiner}
              {caption}
            </span>
          )}
        </span>
        {copyable && (
          <button
            type="button"
            className={styles.copy}
            aria-label={
              copied
                ? frameCopy.codeBlock.copiedName(what)
                : frameCopy.codeBlock.copyName(what)
            }
            onClick={() => void copy()}
          >
            <Icon name="copy" size="icon-embedded" />
            <span>
              {copied ? frameCopy.codeBlock.copied : frameCopy.codeBlock.copy}
            </span>
          </button>
        )}
        {copyable && (
          <span role="status" className={styles.visuallyHidden}>
            {copied ? (
              <span key={copies % 2}>{frameCopy.codeBlock.copied}</span>
            ) : null}
          </span>
        )}
      </div>
      <pre
        ref={ref}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be keyboard-focusable so it can be scrolled without a pointer
        tabIndex={0}
        className={styles.body}
        data-wrap={wrap ? '' : undefined}
        style={{ '--local-max-lines': maxLines }}
      >
        {children}
      </pre>
    </div>
  )
}
